import {
  createSharedProductSnapshot,
  normalizeSharedProductSnapshot,
  productViewFromSharedSnapshot,
  type SharedProductSnapshot,
} from "./shared-product-model";
import type { AppLocale } from "./locale";

const SHARED_PRODUCT_START_PATTERN = /^product_([a-f\d]{36})$/;
const DIRECT_PRODUCT_START_PATTERN =
  /^(?:offer|inline)_([a-f\d]{12})_([a-z\d_-]{1,128})$/i;

export interface DirectProductReference {
  catalogId: string;
  productId: string;
}

export {
  createSharedProductSnapshot,
  productViewFromSharedSnapshot,
  type SharedProductSnapshot,
};

export interface PreparedProductShare {
  preparedMessageId: string;
  preparedMessageExpiresAt: number | null;
  shareUrl: string;
  expiresAt: string;
}

export interface SharedProductResponse {
  createdAt: string;
  expiresAt: string;
  product: SharedProductSnapshot;
}

export class SharedProductApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(code);
  }
}

async function responseBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function parseSharedProductStartParameter(
  value: string | null,
): string | null {
  if (!value) {
    return null;
  }
  return SHARED_PRODUCT_START_PATTERN.exec(value)?.[1] ?? null;
}

export function directProductStartParameter(
  catalogId: string,
  productId: string,
): string | null {
  if (
    !/^[a-f\d]{12}$/i.test(catalogId) ||
    !/^[a-z\d_-]{1,128}$/i.test(productId)
  ) {
    return null;
  }
  return `offer_${catalogId.toLowerCase()}_${productId}`;
}

export function inlineProductStartParameter(
  catalogId: string,
  productId: string,
): string | null {
  if (
    !/^[a-f\d]{12}$/i.test(catalogId) ||
    !/^[a-z\d_-]{1,128}$/i.test(productId)
  ) {
    return null;
  }
  const parameter = `inline_${catalogId.toLowerCase()}_${productId}`;
  return parameter.length <= 512 ? parameter : null;
}

export function parseDirectProductStartParameter(
  value: string | null,
): DirectProductReference | null {
  if (!value) {
    return null;
  }
  const match = DIRECT_PRODUCT_START_PATTERN.exec(value);
  return match
    ? { catalogId: match[1]!.toLowerCase(), productId: match[2]! }
    : null;
}

export function directProductTelegramUrl(
  catalogId: string,
  productId: string,
  botUsername = "KiloPrice_bot",
): string | null {
  const startParameter = directProductStartParameter(catalogId, productId);
  return startParameter
    ? `https://t.me/${botUsername.replace(/^@/, "")}?startapp=${startParameter}`
    : null;
}

export function inlineProductTelegramUrl(
  catalogId: string,
  productId: string,
  botUsername = "KiloPrice_bot",
): string | null {
  const startParameter = inlineProductStartParameter(catalogId, productId);
  return startParameter
    ? `https://t.me/${botUsername.replace(/^@/, "")}?startapp=${startParameter}`
    : null;
}

export async function prepareProductShare(
  initData: string,
  product: SharedProductSnapshot,
  locale: AppLocale,
): Promise<PreparedProductShare> {
  const response = await fetch("/api/shared-products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData, product, locale }),
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new SharedProductApiError(
      typeof body.error === "string" ? body.error : "share_failed",
      response.status,
    );
  }
  if (
    typeof body.preparedMessageId !== "string" ||
    typeof body.shareUrl !== "string" ||
    typeof body.expiresAt !== "string"
  ) {
    throw new SharedProductApiError("invalid_share_response", 502);
  }
  return {
    preparedMessageId: body.preparedMessageId,
    preparedMessageExpiresAt:
      typeof body.preparedMessageExpiresAt === "number"
        ? body.preparedMessageExpiresAt
        : null,
    shareUrl: body.shareUrl,
    expiresAt: body.expiresAt,
  };
}

export async function loadSharedProduct(
  token: string,
): Promise<SharedProductResponse> {
  const response = await fetch(`/api/shared-products/${encodeURIComponent(token)}`, {
    cache: "no-store",
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new SharedProductApiError(
      typeof body.error === "string" ? body.error : "shared_product_failed",
      response.status,
    );
  }
  const product = normalizeSharedProductSnapshot(body.product);
  if (
    !product ||
    typeof body.createdAt !== "string" ||
    typeof body.expiresAt !== "string"
  ) {
    throw new SharedProductApiError("invalid_shared_product_response", 502);
  }
  return {
    createdAt: body.createdAt,
    expiresAt: body.expiresAt,
    product,
  };
}
